// Context
import Modal from "../common/Modal.js";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { baaDefinitions, useAuth } from "../../hooks/useAuth.js";
import Logo from "../../images/logo.js";
import { toast } from "react-toastify";
import VerificationInput from "react-verification-input";
import TermsOfUseText from "./TermsOfUseText.js";
import BaaAgreementText from "./BaaAgreementText.js";
import moment from "moment";
import { audit, defaultDateFormat } from "../../utils/utils.js";
import api from "../../services/api.js";
import sha256 from "crypto-js/sha256";
import Encoder from "crypto-js/enc-hex";
import { useNoteType } from "../../hooks/useNoteType.js";
import Table from "../common/Table.js";
import Switch from "react-switch";
import Form from "../common/Form.js";
import { useChats } from "../../hooks/useChats.js";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFeatures } from "../../hooks/useFeatures.js";
import { buildTrainingUrl } from "../../utils/trainingRouting.js";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  getPasswordPolicyError,
  isServerRejection,
  getServerErrorDetail,
} from "../../utils/passwordPolicy.js";
import {
  computeRequiresNpi,
  isOrdersProviderSupervisor,
  getCredentialsOptions,
  NPI_REQUIRED_ERROR,
} from "../../utils/credentialsUtils.js";
import { buildPatientIdentityContactFormConfig, buildPatientShippingFormConfig } from "./ModalWizardPatientForms";
import AddressAutocomplete from "../common/AddressAutocomplete";
import MedicalLicenseManager from "../profile/MedicalLicenseManager";
import { normalizeLicenses, getPrimaryLicenseNumber, applyLicenses } from "../../utils/licenseUtils.js";

function ModalWizard({ close, isDemoMode = false, demoIsPatient = false }) {
  // Auth
  const { auth, setAuth, hasGroup, termsOfUseVersion, brand, requiredBaas, updateOrgInfo } = useAuth();
  const { hasFeature } = useFeatures();
  const navigate = useNavigate();
  // demoIsPatient lets the demo launcher render patient-flavored steps WITHOUT
  // flipping auth.session.currentOrganizationPlan to "order-patient". That flip
  // would propagate to Routes.js (home-redirect to /peptides) and Peptides.js
  // (PatientPeptideConsentGate) — bleeding patient UI into a provider session.
  const isPatient = demoIsPatient || auth?.session?.currentOrganizationPlan === "order-patient";
  // Current ToS version for this brand. `?? termsOfUseVersion.default` guards a
  // legal-consent field against ever being `undefined` (JSON.stringify silently
  // drops undefined keys on save — the root of the optimalcaregrp onboarding
  // loop). `??` (not `||`) so a legitimate version of 0 is never clobbered.
  const currentTermsVersion = termsOfUseVersion[brand || "default"] ?? termsOfUseVersion.default;
  // Capture the URL the wizard mounted at so a patient who deep-linked to a
  // peptide route (e.g. /peptides?view=cart from a referral link) gets returned
  // there after Finish, not punted to the catalog default.
  const initialPathRef = useRef(typeof window !== "undefined" ? window.location.pathname + window.location.search : "");

  // Info
  const { getDefaultNotesByDoctorType, noteTypes, states, doctorTypes } = useNoteType();
  const { isNoteEnabled } = useChats();

  // Utils
  const [isValid, setIsValid] = useState(false);
  const [displayNoteTypes, setDisplayNoteTypes] = useState([]);
  const [referrals, setReferrals] = useState({});

  // Steps
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const contentRef = useRef(null);

  // Pwd
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // PIN
  const [pin, setPin] = useState("");

  // Language
  const [spen, setSpen] = useState();
  const { t } = useTranslation();

  // Wizard data
  const [orgInfo, setOrgInfo] = useState(() => {
    const base = auth?.orgInfo || {};
    const pending = localStorage.getItem("pendingPracticeName");
    if (!base.officeName) {
      if (pending) {
        return { ...base, officeName: pending };
      }
    }
    return base;
  });
  const [profile, setProfile] = useState({
    ...{
      ...auth?.user,
      firstName: auth?.user?.firstName || "",
      lastName: auth?.user?.lastName || "",
      mobilePhone: auth?.user?.mobilePhone || "",
      custom: {
        transcriptLanguage: "en",
        practiceState: auth?.orgInfo?.officeState || "",
        practiceZip: auth?.orgInfo?.officeZip || "",
        ...auth?.user?.custom,
        // `sameAsBilling` is patient-only. We do NOT default it here (would leak
        // `true` into provider payloads). Patient render path defaults to checked
        // via `profile.custom?.sameAsBilling !== false` in patientShippingFormConfig.
        sameAsBilling: auth?.user?.custom?.sameAsBilling,
        noteSettings: {
          default: {
            hpiStyleMode: "paragraph",
            hpiLengthMode: "comprehensive",
            mdmStyleMode: "paragraph",
            mdmLengthMode: "comprehensive",
            rosMode: "concise",
            physicalExamLengthMode: "comprehensive",
            planOfCareMode: "complex",
            ...auth?.user?.custom?.noteSettings?.default,
          },
        },
        availableNotes: {
          ...auth?.user?.custom?.availableNotes,
        },
      },
    },
  });

  // NPI — a clinician is the encounters "doctor" group OR an orders provider
  // supervisor (peptides/DME providers lack the doctor group). The shared
  // predicate excludes manufacturers; see isOrdersProviderSupervisor.
  const ordersProviderSupervisor = isOrdersProviderSupervisor({
    hasOrders: hasFeature("orders"),
    hasOrdersManagement: hasFeature("ordersManagement"),
    isSupervisor: hasGroup("supervisor"),
  });
  const requiresNPI = useMemo(() => {
    const requiresNPI = computeRequiresNpi({
      hasOrders: hasFeature("orders"),
      isDoctor: hasGroup("doctor"),
      isSupervisor: ordersProviderSupervisor,
      credentials: profile.custom?.credentials,
    });
    if (!requiresNPI) {
      profile.custom.npiNumber = "";
      profile.custom.ptanNumber = "";
      profile.custom.licenseNumber = "";
      profile.custom.licenses = [];
      setProfile({ ...profile });
    }
    return requiresNPI;
    // eslint-disable-next-line
  }, [hasFeature, hasGroup, ordersProviderSupervisor, profile.custom?.credentials]);

  const changeDoctorType = useCallback(
    (val) => {
      const prevType = profile.custom.doctorType;
      profile.custom.doctorType = val;

      // Chiropractors default to the DC credential. Only on the actual switch
      // into "chiropractor" (not the re-call on Next), so a manual change the
      // provider makes afterward is preserved. Staff (MA/RN) never reach this
      // doctor-only step, so their own credentials are untouched.
      if (val === "chiropractor" && prevType !== "chiropractor") {
        profile.custom.credentials = "DC";
      }

      if (!profile.custom.availableNotes) {
        profile.custom.availableNotes = {};
      }
      for (let note of noteTypes) {
        const matchesDoctorType =
          note.doctorTypes === "*" ||
          (Array.isArray(note.doctorTypes) && note.doctorTypes.indexOf(profile.custom.doctorType) !== -1);
        if (!val || matchesDoctorType) {
          profile.custom.availableNotes[note.value] = true;
        } else {
          profile.custom.availableNotes[note.value] = false;
        }
      }

      const defaultNote = getDefaultNotesByDoctorType(profile.custom.doctorType);
      profile.custom.defaultNewNote = defaultNote.newNote;
      profile.custom.defaultImportNote = defaultNote.importNote;

      if (val === "behavioral") {
        profile.custom.ruralMode = true;
      }

      setProfile({ ...profile });
    },
    [noteTypes, profile, getDefaultNotesByDoctorType]
  );

  // Store the sorted note types for display
  const noteEnabled = useCallback(
    (noteValue) => {
      return isNoteEnabled(noteValue, {
        availableNotes: profile.custom?.availableNotes,
        doctorType: profile.custom?.doctorType,
      });
    },
    [profile.custom?.availableNotes, profile.custom?.doctorType, isNoteEnabled]
  );

  const isBaaSigned = useCallback(
    (baaType) =>
      profile.custom?.[`baaSignatureDate_${baaType}`] &&
      profile.custom?.[`baaVersion_${baaType}`] === baaDefinitions[baaType]?.version,
    [profile]
  );

  // Enabled Steps
  const steps = useMemo(() => {
    return {
      welcome: { enabled: true }, // Welcome
      password: {
        enabled: true,
        validate: () => {
          // Single source of truth in utils/passwordPolicy.js — mirrors the
          // server's 12-char minimum so a too-short password is blocked HERE,
          // before the save PUT, instead of failing server-side and being
          // swallowed. Returns the i18n key of the first unmet rule, or null.
          const errorKey = getPasswordPolicyError(password, confirmPassword);
          if (errorKey) {
            return toast.error(t(errorKey));
          }
          return true;
        },
      },
      clinicalRoleSelection: {
        enabled: hasGroup("supervisor") && hasFeature("orders") && !isPatient,
        validate: () => {
          if (!profile.custom.clinicalRole) {
            return toast.error(t("layout.toasts.selectClinicalRole"));
          }
          return true;
        },
      },
      doctorTypeCredentials: {
        // Orders (peptides/DME) providers carry "supervisor", not "doctor", so
        // they must also reach the credentials picker — otherwise they can never
        // select an NPI credential and the NPI step below stays hidden. Same
        // shared predicate as requiresNPI (manufacturer-excluded), so the step
        // and the NPI requirement can't drift apart.
        enabled:
          (hasGroup("doctor") || ordersProviderSupervisor) && profile.custom?.clinicalRole !== "non-clinical" && !isPatient,
        validate: () => {
          if (!profile.custom?.credentials || (profile.custom?.credentials === "Other" && !profile.custom.credentialsOther)) {
            return toast.error(t("wizard.credentials.toastEnter"));
          }

          if (profile.custom.doctorType) {
            changeDoctorType(profile.custom.doctorType);
          }
          return true;
        },
      }, // Doctor Type & Credentials
      pin: {
        enabled:
          ((hasGroup("doctor") && profile.custom?.clinicalRole !== "non-clinical") ||
            (hasFeature("orders") && hasGroup("frontdesk"))) &&
          !isPatient,
        validate: () => {
          if (pin.length !== 4) {
            return toast.error(t("wizard.pin.toastEnter4Digits"));
          }
          return true;
        },
      }, // PIN (skip for front desk and sales-rep)
      // Chiropractors run the separate ChiroApp shell with its own fixed note
      // catalog + AI note, so the generic note-type / note-style pickers would
      // only crowd their onboarding with notes they never use. Skip both.
      noteTypes: { enabled: hasFeature("encounters") && !isPatient && profile.custom?.doctorType !== "chiropractor" }, // Note Types (skip for orders org / chiro)
      noteStyles: { enabled: hasFeature("encounters") && !isPatient && profile.custom?.doctorType !== "chiropractor" }, // Note Styles (skip for orders org / chiro)
      personalInformation: {
        enabled: true,
        validate: () => {
          if (!profile.firstName) {
            return toast.error(t("wizard.profile.toast.enterFirstName"));
          }
          if (!profile.lastName) {
            return toast.error(t("wizard.profile.toast.enterLastName"));
          }
          if (!profile.mobilePhone) {
            return toast.error(t("wizard.profile.toast.enterPhone"));
          }

          // Validate provider supervisor-required fields
          // Practice name required for provider supervisors in orders org
          if (hasGroup("supervisor") && !hasFeature("ordersManagement")) {
            if (!orgInfo?.officeName) {
              return toast.error(t("layout.toasts.officeNameRequired"));
            }
            if (!orgInfo?.officeAddress) {
              return toast.error(t("layout.toasts.officeAddressRequired"));
            }

            if (!orgInfo?.notUS && !orgInfo.officeState) {
              return toast.error(t("layout.toasts.stateRequired"));
            }

            if (!orgInfo?.notUS && !orgInfo.officeZip) {
              return toast.error(t("layout.toasts.zipCodeRequired"));
            }
          }

          // Wound care validation for clinical providers
          if (
            hasFeature("encounters") &&
            hasGroup("doctor") &&
            Object.keys(profile.custom.availableNotes || {}).find((n) => {
              return n.indexOf("woundcare") !== -1 && noteEnabled(n);
            })
          ) {
            if (!profile.custom?.practiceState && !orgInfo?.officeState && !orgInfo?.notUS) {
              return toast.error(t("wizard.profile.toast.selectState"));
            }
            if (!profile.custom?.practiceZip && !orgInfo?.officeZip && !orgInfo?.notUS) {
              return toast.error(t("wizard.profile.toast.insertZip"));
            }
          }

          // NPI is provider-only. The fields themselves hide when isPatient
          // (see personalInfoFormConfig show predicates), but this validate
          // also has to skip the check — otherwise a dev whose real auth has
          // doctor group + NPI credentials gets blocked at the patient demo.
          if (requiresNPI && !isPatient && !profile.custom?.npiNumber) {
            return toast.error(NPI_REQUIRED_ERROR);
          }
          return true;
        },
      }, // Personal Information
      language: { enabled: hasFeature("encounters") && !isPatient }, // Language (skip for orders org and sales-rep)
      referral: { enabled: hasFeature("encounters") && !isPatient }, // Referral
      patientIdentityContact: {
        enabled: isPatient,
        validate: () => {
          if (!profile.dob) {
            return toast.error(t("wizard.patientIdentity.toast.dobRequired"));
          }
          if (!profile.custom?.preferredContactChannel) {
            return toast.error(t("wizard.patientIdentity.toast.channelRequired"));
          }
          return true;
        },
      }, // Patient — confirm identity + contact prefs + emergency contact
      patientShipping: {
        enabled: isPatient,
        validate: () => {
          const ship = profile.custom?.shippingAddress || {};
          if (!ship.street || !ship.city || !ship.state || !ship.zip) {
            return toast.error(t("wizard.patientShipping.toast.requiredFields"));
          }
          // US 5-digit or ZIP+4 only. APO/FPO addresses (state codes AE/AP/AA) use 5-digit but
          // require additional state validation; not in scope for v1.
          if (!/^\d{5}(-\d{4})?$/.test(ship.zip)) {
            return toast.error(t("wizard.patientShipping.toast.zipFormat"));
          }
          if (profile.custom?.sameAsBilling === false) {
            const bill = profile.custom?.billingAddress || {};
            if (!bill.street || !bill.city || !bill.state || !bill.zip) {
              return toast.error(t("wizard.patientShipping.toast.billingRequired"));
            }
          }
          return true;
        },
      }, // Patient — shipping address (+ optional billing if not same)
      lucasBaa: {
        enabled: requiredBaas.includes("lucas") && !isPatient,
        validate: () => {
          if (!isBaaSigned("lucas")) {
            return toast.error(t("wizard.baa.signRequired"));
          }
          return true;
        },
      },
      shorelineBaa: {
        enabled: requiredBaas.includes("shoreline") && !isPatient,
        validate: () => {
          if (!isBaaSigned("shoreline")) {
            return toast.error(t("wizard.baa.signRequired"));
          }
          return true;
        },
      },
      meridianBaa: {
        enabled: requiredBaas.includes("meridian") && !isPatient,
        validate: () => {
          if (!isBaaSigned("meridian")) {
            return toast.error(t("wizard.baa.signRequired"));
          }
          return true;
        },
      },
      gtmBaa: {
        enabled: requiredBaas.includes("gtm") && !isPatient,
        validate: () => {
          if (!isBaaSigned("gtm")) {
            return toast.error(t("wizard.baa.signRequired"));
          }
          return true;
        },
      },
      optimalcaregrpBaa: {
        enabled: requiredBaas.includes("optimalcaregrp") && !isPatient,
        validate: () => {
          if (!isBaaSigned("optimalcaregrp")) {
            return toast.error(t("wizard.baa.signRequired"));
          }
          return true;
        },
      },
      baaAndTerms: { enabled: true },
    };
  }, [
    hasGroup,
    requiredBaas,
    isBaaSigned,
    hasFeature,
    password,
    confirmPassword,
    t,
    changeDoctorType,
    pin.length,
    orgInfo,
    profile,
    noteEnabled,
    requiresNPI,
    ordersProviderSupervisor,
    isPatient,
  ]);

  const enabledStepNumbers = useMemo(() => {
    return Object.keys(steps).filter((step) => steps[step].enabled);
  }, [steps]);

  const step = enabledStepNumbers[currentStepIndex];

  // Helper to get the initial sorted note types (prechecked at top)
  const getInitialSortedNoteTypes = () => {
    return [...noteTypes.filter((n) => noteEnabled(n.value)), ...noteTypes.filter((n) => !noteEnabled(n.value))];
  };

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      // Scroll the parent modal-content container to top
      const modalContent = contentRef.current.closest(".modal-content");
      if (modalContent) {
        modalContent.scrollTop = 0;
      }
    }
  }, [currentStepIndex]);

  // On mount, or when doctorType changes, update displayNoteTypes
  useEffect(() => {
    setDisplayNoteTypes(getInitialSortedNoteTypes());
    // eslint-disable-next-line
  }, [profile.custom.doctorType, profile.custom.availableNotes, noteTypes]);

  // Setters
  const setProfileValue = (key) => (e) => {
    // Defensive: DatePicker (and other custom inputs) call onChange(rawValue);
    // standard <input>/<select> pass an event with target.value. Match the
    // pattern setOrgInfoValue / setProfileCustomValue use elsewhere.
    const value = e?.target ? e.target.value : e;
    profile[key] = value;
    setProfile({ ...profile });
  };

  const setOrgInfoValue = (key) => (e) => {
    let value = e.target ? e.target.value : e;
    orgInfo[key] = value;
    setOrgInfo({ ...orgInfo });
  };

  const setProfileCustomValue = (key) => (e) => {
    profile.custom = profile.custom || {};
    profile.custom[key] = e.target ? e.target.value : e;
    setProfile({ ...profile });
  };

  // Generic nested-custom setter used for shipping/billing addresses + emergency
  // contact. One pattern, one helper — accepts either a DOM event OR a raw value
  // (PhoneInput passes the formatted string directly).
  const setNestedCustom = (parentKey, childKey) => (eOrVal) => {
    const value = eOrVal && eOrVal.target ? eOrVal.target.value : eOrVal;
    profile.custom = profile.custom || {};
    profile.custom[parentKey] = { ...(profile.custom[parentKey] || {}), [childKey]: value };
    setProfile({ ...profile });
  };

  const toggleSameAsBilling = (e) => {
    const checked = e.target.checked;
    profile.custom = profile.custom || {};
    profile.custom.sameAsBilling = checked;
    setProfile({ ...profile });
  };

  const setStateValue = (e) => {
    const value = e.target ? e.target.value : e;
    setProfileCustomValue("practiceState")({ target: { value } });
    if (hasGroup("supervisor")) {
      setOrgInfoValue("officeState")({ target: { value } });
    }
  };

  const setZipValue = (e) => {
    const value = e.target ? e.target.value : e;
    setProfileCustomValue("practiceZip")({ target: { value } });
    if (hasGroup("supervisor")) {
      setOrgInfoValue("officeZip")({ target: { value } });
    }
  };

  const setReferralValue = (key) => (e) => {
    if (e.target.checked) referrals[key] = true;
    else delete referrals[key];
    setReferrals({ ...referrals });
  };

  // Step 2: Password Setup Form Config
  // Live per-field validity for the inline red-glow (reuses the existing
  // `.pwd-wrapper.error input` ring in _login.scss). The New Password field
  // rings red when its own value fails any policy rule — length (the 12-char
  // minimum) or complexity; passing it as its own confirmation neutralizes the
  // mismatch rule so this reflects only the field itself. The Confirm field
  // rings red only once it's non-empty and doesn't match. Both clear the moment
  // the field becomes acceptable, so the glow tracks validity as the user types.
  const newPasswordInvalid = password.length > 0 && getPasswordPolicyError(password, password) !== null;
  const confirmPasswordInvalid = confirmPassword.length > 0 && confirmPassword !== password;
  const passwordFormConfig = [
    {
      type: "content",
      content: <h3>{t("wizard.password.title")}</h3>,
    },
    {
      type: "content",
      content: (
        <div className="login-form">
          <div className={`pwd-wrapper${newPasswordInvalid ? " error" : ""}`}>
            <input
              value={password}
              type={showPassword ? "text" : "password"}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("wizard.password.new")}
              autoComplete="password"
              tabIndex="1"
              aria-invalid={newPasswordInvalid}
            />
            <button className="pwd-btn" onClick={() => setShowPassword(!!!showPassword)}>
              {showPassword ? (
                <i className="notranslate material-icons">visibility_off</i>
              ) : (
                <i className="notranslate material-icons">visibility</i>
              )}
            </button>
          </div>
          <div className={`pwd-wrapper${confirmPasswordInvalid ? " error" : ""}`}>
            <input
              value={confirmPassword}
              type={showConfirmPassword ? "text" : "password"}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t("wizard.password.confirm")}
              autoComplete="password"
              tabIndex="2"
              aria-invalid={confirmPasswordInvalid}
            />
            <button className="pwd-btn" onClick={() => setShowConfirmPassword(!!!showConfirmPassword)}>
              {showPassword ? (
                <i className="notranslate material-icons">visibility_off</i>
              ) : (
                <i className="notranslate material-icons">visibility</i>
              )}
            </button>
          </div>
        </div>
      ),
    },
    {
      type: "content",
      content: <p className="mt-20">{t("wizard.password.requirementsLead")}</p>,
    },
    {
      type: "content",
      content: (
        <ul className="password-requirements">
          <li className={password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH ? "error" : ""}>
            <span className="material-symbols-outlined error">error</span>
            <span className="material-symbols-outlined success">check_circle</span>
            {t("auth.changePassword.req.length")}
          </li>
          <li className={password === password.toUpperCase() ? "error" : ""}>
            <span className="material-symbols-outlined error">error</span>
            <span className="material-symbols-outlined success">check_circle</span>
            {t("auth.changePassword.req.lowercase")}
          </li>
          <li className={password === password.toLowerCase() ? "error" : ""}>
            <span className="material-symbols-outlined error">error</span>
            <span className="material-symbols-outlined success">check_circle</span>
            {t("auth.changePassword.req.uppercase")}
          </li>
          <li className={!/\d/.test(password) ? "error" : ""}>
            <span className="material-symbols-outlined error">error</span>
            <span className="material-symbols-outlined success">check_circle</span>
            {t("auth.changePassword.req.number")}
          </li>
          <li className={!/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password) ? "error" : ""}>
            <span className="material-symbols-outlined error">error</span>
            <span className="material-symbols-outlined success">check_circle</span>
            {t("auth.changePassword.req.special")}
          </li>
          <li className={password !== confirmPassword ? "error" : ""}>
            <span className="material-symbols-outlined error">error</span>
            <span className="material-symbols-outlined success">check_circle</span>
            {t("auth.changePassword.mismatch")}
          </li>
        </ul>
      ),
    },
  ];

  // Step 3.5: Clinical Role Selection Form Config (Supervisors Only)
  const supervisorClinicalRoleFormConfig = [
    {
      type: "content",
      content: <h5>{t("wizard.clinicalRole.title")}</h5>,
    },
    {
      type: "content",
      content: <p>{t("wizard.clinicalRole.description")}</p>,
    },
    {
      label: t("wizard.clinicalRole.roleTypeLabel"),
      type: "select",
      value: profile.custom?.clinicalRole || "",
      onChange: setProfileCustomValue("clinicalRole"),
      required: true,
      options: [
        { key: "", label: t("wizard.clinicalRole.selectRoleType") },
        { key: "clinical", label: t("wizard.clinicalRole.clinical") },
        { key: "non-clinical", label: t("wizard.clinicalRole.nonClinical") },
      ],
    },
  ];

  // Step 4: Credentials Form Config
  const credentialsFormConfig = [
    {
      type: "content",
      content: <h5>{t("wizard.doctorType.prompt")}</h5>,
    },
    {
      label: t("wizard.doctorType.providerType"),
      type: "select",
      value: profile.custom?.doctorType || "",
      show: hasFeature("encounters"),
      onChange: (e) => changeDoctorType(e.target.value),
      options: [
        { key: "", label: t("wizard.doctorType.generalist") },
        ...Object.keys(doctorTypes).map((t) => ({
          key: t,
          label: doctorTypes[t],
        })),
      ],
    },
    {
      label: t("wizard.credentials.label"),
      type: "select",
      value: profile.custom?.credentials || "",
      onChange: setProfileCustomValue("credentials"),
      required: true,
      options: getCredentialsOptions(t),
    },
    {
      label: t("wizard.credentials.other"),
      type: "text",
      value: profile.custom?.credentialsOther || "",
      onChange: setProfileCustomValue("credentialsOther"),
      show: profile.custom?.credentials === "Other",
      required: profile.custom?.credentials === "Other",
    },
  ];

  // Step 7: Personal Information Form Config
  const personalInfoFormConfig = [
    {
      type: "content",
      content: <h5>{t("wizard.profile.title")}</h5>,
    },
    {
      label: t("wizard.profile.firstName"),
      type: "text",
      value: profile.firstName || "",
      onChange: setProfileValue("firstName"),
      required: true,
    },
    {
      label: t("wizard.profile.lastName"),
      type: "text",
      value: profile.lastName || "",
      onChange: setProfileValue("lastName"),
      required: true,
    },
    {
      label: t("wizard.profile.phone"),
      type: "phone",
      value: profile.mobilePhone || "",
      onChange: setProfileValue("mobilePhone"),
      required: true,
    },
    {
      label: t("wizard.profile.officeName"),
      type: "text",
      value: orgInfo?.officeName || "",
      show: !hasFeature("ordersManagement") && !isPatient,
      onChange: setOrgInfoValue("officeName"),
      placeholder: t("wizard.profile.officeNamePlaceholder"),
      disabled: !hasGroup("supervisor"),
      required: hasGroup("supervisor"),
    },
    // Office Address — uses Google Places Autocomplete (see
    // AddressAutocomplete / googleMapsLoader). When the user picks a
    // suggestion, the parsed address auto-fills the State and Zip fields
    // below in addition to writing the street to orgInfo.officeAddress.
    // Falls back to a plain text input if the Maps API key isn't
    // configured (REACT_APP_GOOGLE_MAPS_API_KEY).
    {
      type: "content",
      show: !hasFeature("ordersManagement") && !isPatient,
      content: (
        <div className={`row-form vertical-align${hasGroup("supervisor") ? " required" : ""}`}>
          <label>
            {t("wizard.profile.officeAddress")}
            {hasGroup("supervisor") && <span className="required-star">*</span>}
          </label>
          <AddressAutocomplete
            value={orgInfo?.officeAddress || ""}
            onChange={(v) => setOrgInfoValue("officeAddress")({ target: { value: v } })}
            onPlaceSelected={(p) => {
              // Street first — overwrite with just the parsed street portion
              // so the field shows "185 SW 7th St" not the full formatted
              // address (city/state/zip live in their own fields below).
              setOrgInfoValue("officeAddress")({ target: { value: p.address || p.formatted || "" } });
              if (p.city && hasGroup("supervisor")) {
                setOrgInfoValue("officeCity")({ target: { value: p.city } });
              }
              if (p.state) setStateValue({ target: { value: p.state } });
              if (p.zip) setZipValue({ target: { value: p.zip } });
              if (p.country && hasGroup("supervisor")) {
                setOrgInfoValue("officeCountry")({ target: { value: p.country } });
                // Flip notUS automatically if a non-US country was picked.
                setOrgInfoValue("notUS")({ target: { value: p.country !== "US" } });
              }
            }}
            placeholder={t("wizard.profile.officeAddressPlaceholder")}
            inputProps={{
              disabled: !hasGroup("supervisor"),
            }}
          />
        </div>
      ),
    },
    {
      type: "content",
      content: (
        <div className="row-form vertical-align">
          <label>{t("wizard.profile.notInUS")}</label>
          <Switch
            checked={orgInfo?.notUS}
            onChange={setOrgInfoValue("notUS")}
            uncheckedIcon={false}
            checkedIcon={false}
            height={24}
            width={45}
            className="mr-10"
          />
        </div>
      ),
      show: !hasFeature("ordersManagement") && !isPatient,
      disabled: !hasGroup("supervisor"),
      required: hasGroup("supervisor"),
    },
    {
      label: t("wizard.profile.state"),
      type: "select",
      value: profile.custom?.practiceState || orgInfo?.officeState || "",
      onChange: setStateValue,
      show: !orgInfo?.notUS && !hasFeature("ordersManagement") && !isPatient,
      required: !orgInfo?.notUS,
      options: [
        { key: "", label: t("wizard.profile.selectStateOption") },
        ...states.map((state) => ({
          key: state.code,
          label: state.name,
        })),
      ],
    },
    {
      label: t("wizard.profile.zipcode"),
      type: "text",
      value: profile.custom?.practiceZip || orgInfo?.officeZip || "",
      show: !orgInfo?.notUS && !hasFeature("ordersManagement") && !isPatient,
      onChange: setZipValue,
      required: !orgInfo?.notUS,
    },
    {
      label: t("wizard.profile.npiNumber"),
      type: "text",
      value: profile.custom?.npiNumber || "",
      // Digits-only, hard-capped at 10 — same input hygiene the profile-edit NPI
      // field uses, so onboarding (the FIRST place a provider types their NPI)
      // can't capture letters/dashes or an over-length value that would later
      // fail NPPES verification. Mirrors the ptanNumber sanitizer below.
      onChange: (e) => {
        const val = e.target ? e.target.value : e;
        const cleaned = val.replace(/\D/g, "").slice(0, 10);
        setProfileCustomValue("npiNumber")({ target: { value: cleaned } });
      },
      show: requiresNPI && !isPatient,
      placeholder: t("wizard.profile.npiNumberPlaceholder"),
      required: requiresNPI,
      hint: t("npiRoster.nameHint", "Enter your name exactly as it appears in the NPPES NPI Registry."),
    },
    {
      label: t("wizard.profile.ptanNumber"),
      type: "text",
      value: profile.custom?.ptanNumber || "",
      onChange: (e) => {
        const val = e.target ? e.target.value : e;
        const cleaned = val.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10);
        setProfileCustomValue("ptanNumber")({ target: { value: cleaned } });
      },
      show: requiresNPI && !isPatient,
      placeholder: t("wizard.profile.ptanNumberPlaceholder"),
      required: false,
      maxLength: 10,
    },
    {
      type: "content",
      show: requiresNPI && !isPatient,
      content: (
        <MedicalLicenseManager
          value={normalizeLicenses(profile.custom)}
          credential={profile.custom?.credentials}
          states={states}
          onChange={(licenses) => applyLicenses(setProfileCustomValue, licenses)}
        />
      ),
    },
  ];

  // Step 8: Language Form Config
  const lanuageFormConfig = [
    {
      type: "content",
      content: <h5>{t("wizard.language.primaryQuestion")}</h5>,
    },
    {
      type: "select",
      value: profile.custom?.transcriptLanguage || "",
      onChange: setProfileCustomValue("transcriptLanguage"),
      hideLabel: true,
      options: [
        { key: "en", label: t("wizard.language.options.en") },
        { key: "es", label: t("wizard.language.options.es") },
        { key: "spen", label: t("wizard.language.options.spen") },
        { key: "ar", label: t("wizard.language.options.ar") },
        { key: "ko", label: t("wizard.language.options.ko") },
        { key: "auto", label: t("wizard.language.options.auto") },
      ],
    },
    {
      type: "content",
      content: <h5>{t("wizard.language.speakSpanishQuestion")}</h5>,
    },
    {
      type: "select",
      value: spen || "",
      hideLabel: true,
      onChange: (e) => setSpen(e.target.value),
      options: [
        { key: "", label: "" },
        { key: "yes", label: t("wizard.language.yes") },
        { key: "no", label: t("wizard.language.no") },
      ],
    },
  ];

  // Step 9: Referral Form Config
  const referralFormConfig = [
    {
      type: "content",
      content: <h5>{t("wizard.referral.title")}</h5>,
    },
    {
      type: "content",
      content: (
        <div>
          {[
            { key: "Instagram", label: t("wizard.referral.options.Instagram") },
            { key: "Facebook", label: t("wizard.referral.options.Facebook") },
            { key: "LinkedIn", label: t("wizard.referral.options.LinkedIn") },
            { key: "Twitter", label: t("wizard.referral.options.Twitter") },
            { key: "TikTok", label: t("wizard.referral.options.TikTok") },
            { key: "YouTube", label: t("wizard.referral.options.YouTube") },
            { key: "Google Search", label: t("wizard.referral.options.GoogleSearch") },
            { key: "Word of Mouth", label: t("wizard.referral.options.WordOfMouth") },
            { key: "Friend", label: t("wizard.referral.options.Friend") },
            { key: "Doctor Referral", label: t("wizard.referral.options.DoctorReferral") },
            { key: "Conference", label: t("wizard.referral.options.Conference") },
            { key: "Ad", label: t("wizard.referral.options.Ad") },
            { key: "Podcast", label: t("wizard.referral.options.Podcast") },
          ].map((item) => (
            <div className="row-form vertical-align" key={item.key}>
              <input type="checkbox" checked={referrals[item.key] || false} onChange={setReferralValue(item.key)} />
              <label style={{ marginLeft: 10 }}>{item.label}</label>
            </div>
          ))}
        </div>
      ),
    },
  ];

  // Step (patient): Identity + Contact Prefs + Emergency Contact Form Config
  // Definition lives in ModalWizardPatientForms.js — pass deps as args.
  const patientIdentityContactFormConfig = buildPatientIdentityContactFormConfig({
    t,
    profile,
    setProfileValue,
    setProfileCustomValue,
    setNestedCustom,
  });

  // Step (patient): Shipping (+ optional billing) Form Config
  // Definition lives in ModalWizardPatientForms.js — pass deps as args.
  const patientShippingFormConfig = buildPatientShippingFormConfig({
    t,
    profile,
    setNestedCustom,
    toggleSameAsBilling,
    states,
  });

  // Move between steps
  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const nextStep = () => {
    // Validation via step validate function
    const currentStep = steps[enabledStepNumbers[currentStepIndex]];
    if (currentStep && currentStep.validate) {
      let valid = currentStep.validate();
      if (valid !== true) {
        return;
      }
    }

    if (currentStepIndex < enabledStepNumbers.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  // Signing functions
  const sign = () => {
    profile.custom.doctorConsentAuthorizeSignature = profile.firstName + " " + profile.lastName;
    profile.custom.doctorConsentAuthorizeSignatureDate = new Date();
    profile.custom.doctorConsentAuthorizeVersion = currentTermsVersion;
    setProfile({ ...profile });
  };

  const signBaa = (baaType) => {
    profile.custom[`baaSignature_${baaType}`] = profile.firstName + " " + profile.lastName;
    profile.custom[`baaSignatureDate_${baaType}`] = new Date();
    profile.custom[`baaVersion_${baaType}`] = baaDefinitions[baaType]?.version || 1;
    setProfile({ ...profile });
  };

  const complete = async () => {
    try {
      // Demo mode short-circuit — same as the existing provider demo path.
      // The launcher renders this wizard with isDemoMode={true}; complete()
      // returns immediately so api.updateProfile / audit / localStorage are
      // never touched. closeDemo restores auth from the launcher's backup ref.
      if (isDemoMode) {
        close();
        return;
      }

      // Two-tab race: if a sibling tab already completed onboarding, just close + redirect
      // without re-saving stale form state.
      if (isPatient && auth.user.custom?.patientOnboardingCompleted === true) {
        close();
        const initial = initialPathRef.current || "";
        const target = initial.startsWith("/peptides") ? initial : "/peptides?view=catalog";
        navigate(target);
        return;
      }

      // Require BOTH the signature date AND the current version stamp — mirrors
      // isBaaSigned. A half-signed profile (date saved, version dropped by the
      // old junk-brand serialization bug) must re-sign; letting it through here
      // would re-send the stale signature WITHOUT a version and re-enter the
      // onboarding loop.
      if (
        !profile.custom.doctorConsentAuthorizeSignatureDate ||
        profile.custom.doctorConsentAuthorizeVersion !== currentTermsVersion
      ) {
        return toast.error(t("wizard.terms.signRequired"));
      }

      // Hash PIN if step 3 was shown (not for frontdesk and sales-rep)
      let pinHashed = "";
      if (pin) {
        const hash = sha256(pin);
        pinHashed = Encoder.stringify(hash).toUpperCase();
      }

      let referralsStr = Object.keys(referrals).join(",");

      // Check if this is a first-time user BEFORE updating auth
      const isFirstTimeUser = !auth.user.custom?.doctorConsentAuthorizeVersion;
      const hasSeenTraining = !!auth.user.custom?.trainingWizardCompleted;
      const isManufacturer = auth.session?.currentOrganizationPlan === "order-manufacturer";
      // Peptides-only providers shouldn't be sent to DME training; no peptides training site exists yet.
      const isPeptidesOnly = auth?.orgInfo?.productLines?.peptides === true && auth?.orgInfo?.productLines?.dme !== true;
      const shouldLaunchTraining = isFirstTimeUser && !hasSeenTraining && !isManufacturer && !isPeptidesOnly;

      // Normalize multi-state licenses once. Persist the array AND keep the
      // legacy `licenseNumber` string as a mirror of the primary so existing
      // readers (SignerContactCard, backend, PDFs) are unaffected. Also covers
      // legacy single-license profiles editing through the wizard (seed-on-load).
      const normalizedLicenses = normalizeLicenses(profile.custom);

      // Prepare base profile update
      let updatedProfile = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        mobilePhone: profile.mobilePhone,
        // dob is a top-level user-vertex field captured by patientIdentityContact;
        // it lives on profile.dob, NOT profile.custom.dob.
        dob: profile.dob,
        custom: {
          doctorConsentAuthorizeSignature: profile.custom.doctorConsentAuthorizeSignature,
          doctorConsentAuthorizeSignatureDate: profile.custom.doctorConsentAuthorizeSignatureDate,
          doctorConsentAuthorizeVersion: profile.custom.doctorConsentAuthorizeVersion,
          referrals: referralsStr,
          notUS: profile.custom.notUS,
          signPIN: pinHashed,
          clinicalRole: profile.custom.clinicalRole,
          address: profile.custom.address,
          practiceState: profile.custom.practiceState,
          practiceZip: profile.custom.practiceZip,
          npiNumber: profile.custom.npiNumber,
          ptanNumber: profile.custom.ptanNumber,
          licenseNumber: getPrimaryLicenseNumber(normalizedLicenses),
          licenses: normalizedLicenses,
          availableNotes: profile.custom.availableNotes,
          defaultNewNote: profile.custom.defaultNewNote,
          defaultImportNote: profile.custom.defaultImportNote,
          doctorType: profile.custom.doctorType,
          ruralMode: profile.custom.ruralMode,
          credentials: profile.custom.credentials,
          noteSettings: profile.custom.noteSettings,
          transcriptLanguage:
            profile.custom.transcriptLanguage === "en" && spen === "yes" ? "spen" : profile.custom.transcriptLanguage,
          ...(shouldLaunchTraining ? { trainingWizardCompleted: true } : {}),
          // Preserve recordingHealthCheckCompleted for first-time users so the healthcheck modal is triggered
          ...(isFirstTimeUser ? { recordingHealthCheckCompleted: false } : {}),
          // Patient-only onboarding fields. Gated by isPatient so a provider session
          // never persists shippingAddress / sameAsBilling / etc.
          ...(isPatient
            ? {
                shippingAddress: profile.custom.shippingAddress,
                billingAddress: profile.custom.billingAddress,
                sameAsBilling: profile.custom.sameAsBilling,
                emergencyContact: profile.custom.emergencyContact,
                preferredContactChannel: profile.custom.preferredContactChannel,
                patientOnboardingCompleted: true,
              }
            : {}),
        },
        password: password,
      };

      // Add BAA signatures
      requiredBaas.forEach((type) => {
        updatedProfile.custom[`baaSignature_${type}`] = profile.custom[`baaSignature_${type}`];
        updatedProfile.custom[`baaSignatureDate_${type}`] = profile.custom[`baaSignatureDate_${type}`];
        updatedProfile.custom[`baaVersion_${type}`] = profile.custom[`baaVersion_${type}`];
      });

      // Silent NPPES verification. The provider NEVER sees the result and is
      // NEVER blocked — verification status is advisory data owned by the
      // order-manufacturer (see NpiRosterModal). We still collect the NPI (it
      // stays a required field, validated at the personalInformation step ~306),
      // but the result only surfaces to the manufacturer. Fire-and-forget;
      // swallow all errors so onboarding always completes.
      // TODO(S-NPI): backend verifies authoritatively on profile-write; this
      // client call is best-effort warmup and must never gate the wizard.
      if (requiresNPI && !isPatient && profile.custom?.npiNumber) {
        api
          .verifyNpi(String(profile.custom.npiNumber).replace(/\D/g, ""), profile.firstName, profile.lastName)
          .catch((npiErr) => console.warn("ModalWizard: silent NPI verification failed (non-blocking)", npiErr));
      }

      let profileSaved = false;
      try {
        const response = await api.updateProfile(updatedProfile);
        // Server response validation. axios returns { data, status, ... }; the
        // LucasAI backend convention surfaces failures as `{ ok: false, errors: [...] }`
        // (HTTP 200 but logical failure). Throw so the catch path runs.
        if (response && response.data && response.data.ok === false) {
          // HTTP-200 logical rejection ({ ok:false, errors:[] }). Tag it so the
          // catch classifies it as a SERVER REJECTION (surface + stay on wizard),
          // not a transient/offline failure — otherwise this co-located channel
          // re-opens the exact silent-lockout the 4xx branch closes.
          const rejection = new Error((response.data.errors && response.data.errors[0]) || "Update profile rejected");
          rejection.isServerRejection = true;
          rejection.serverDetail = (response.data.errors && response.data.errors[0]) || null;
          throw rejection;
        }
        profileSaved = true;
      } catch (apiError) {
        // A SERVER REJECTION (HTTP 4xx — e.g. the 12-char password policy → 422)
        // is NOT an offline situation and must NOT be swallowed as "saved
        // offline, will retry". The retry path below strips the password (so it
        // can never re-set it) and only runs after a successful login — which is
        // impossible if a first-time password never persisted. Swallowing it is
        // exactly what silently locked new users out of the account they just
        // created. Surface the server's reason and keep the user on the wizard
        // (do not close/navigate) so they can fix it and retry.
        if (isServerRejection(apiError)) {
          // Breadcrumb: a user stuck on the wizard by a server rejection leaves a
          // client-side trace for on-call (the offline path below logs too).
          console.warn("Wizard profile save rejected by server (staying on wizard):", apiError);
          toast.error(getServerErrorDetail(apiError) || t("wizard.toast.completeError"));
          return;
        }
        console.warn("Wizard profile save failed, storing locally for retry:", apiError);
        // Strip sensitive fields before persisting to localStorage.
        const { password: _pw, ...safeProfile } = updatedProfile;
        const { signPIN: _pin, ...safeCustom } = safeProfile.custom;
        // Patient PHI must not persist in localStorage offline fallback (HIPAA / shared-device risk)
        delete safeCustom.shippingAddress;
        delete safeCustom.billingAddress;
        delete safeCustom.emergencyContact;
        delete safeCustom.preferredContactChannel;
        // DOB lives on safeProfile top-level, not custom — strip there.
        delete safeProfile.dob;
        safeProfile.custom = safeCustom;
        localStorage.setItem("pendingWizardProfile", JSON.stringify(safeProfile));
        if (hasFeature("orders") && hasGroup("supervisor") && orgInfo) {
          localStorage.setItem("pendingWizardOrgInfo", JSON.stringify(orgInfo));
        }
        toast.warn(t("wizard.toast.savedOffline", "Your setup was saved locally and will sync when your connection improves."));
      }

      if (profileSaved && hasFeature("orders") && hasGroup("supervisor")) {
        updateOrgInfo(orgInfo);
      }

      localStorage.removeItem("pendingPracticeName");
      audit(isPatient ? "Patient Onboarding Completed" : "Wizard Completed", auth.user.email);

      toast.info(t("wizard.toast.welcome"));

      // Patient early-return MUST run before provider-only state mutations
      // (trainingWizardCompleted / recordingHealthCheckCompleted) so a patient
      // session never has those provider-flow flags written.
      auth.user = { ...auth.user, ...profile };
      setAuth({ ...auth });
      close();

      if (isPatient) {
        // Demo mode: skip the catalog redirect — closeDemo (the launcher's
        // close handler) has already restored the developer's original route
        // and auth state. Forcing /peptides?view=catalog here would overwrite
        // that restore.
        if (isDemoMode) {
          return;
        }
        // Replay the original deep-link if it pointed at a peptide route, else default to catalog.
        const initial = initialPathRef.current || "";
        const target = initial.startsWith("/peptides") ? initial : "/peptides?view=catalog";
        navigate(target);
        return;
      }

      // Provider-only post-save state mutations
      if (shouldLaunchTraining) {
        profile.custom.trainingWizardCompleted = true;
      }
      if (isFirstTimeUser) {
        profile.custom.recordingHealthCheckCompleted = false;
      }

      // Redirect to training simulator ONLY for first-time users (not users re-signing terms)
      if (shouldLaunchTraining) {
        const trainingUrl = buildTrainingUrl({
          currentPath: window.location.pathname,
          previousPath: sessionStorage.getItem("trainingRedirectPreviousPath") || "",
          hasEncounters: hasFeature("encounters"),
          hasOrders: hasFeature("orders"),
          hasOrdersManagement: hasFeature("ordersManagement"),
          brand,
        });

        // Open training in new window
        window.open(trainingUrl, "_blank");
      }
    } catch (error) {
      console.error("Error completing the wizard:", error);
      toast.error(t("wizard.toast.completeError"));
    }
  };

  const setNoteAvailability = (type, value) => {
    profile.custom.availableNotes[type] = value;
    setProfile({ ...profile });
  };

  const changeStyle = (type, val) => {
    profile.custom.noteSettings.default[type] = val;
    setProfile({ ...profile });
  };

  const config = {
    columns: [
      {
        key: "enabled",
        header: t("wizard.table.enable"),
        template: (item) => (
          <div>
            <input
              type="checkbox"
              checked={noteEnabled(item.value)}
              onChange={(e) => setNoteAvailability(item.value, e.target.checked)}
            />
          </div>
        ),
      },
      {
        key: "label",
        header: t("wizard.table.type"),
        template: (item) => (
          <div>
            <div>{item.label}</div>
            <div className="meta">{item.description}</div>
          </div>
        ),
      },
    ],
  };

  let organization;
  switch (brand) {
    case "rpg":
      organization = "RPG";
      break;
    case "healcore":
      organization = "Healcore";
      break;
    default:
      organization = "Lucas";
      break;
  }

  return (
    <Modal
      close={close}
      hideClose={!isDemoMode}
      title={<Logo className="pointer" />}
      show={true}
      size="full-screen"
      className={"wizard-modal" + (isPatient ? " wizard-modal-patient" : "")}
    >
      <div className="wizard-modal-content" ref={contentRef}>
        {/* BODY */}

        {/* WELCOME */}
        {step === "welcome" &&
          (isPatient ? (
            // Patient peptides flow: top-anchored, heading centered horizontally
            // (matches the provider welcome via `.flex-center` + `align-items: center`).
            // Body paragraph sits in a max-width block beneath so wrapped lines stay
            // readable inside the wider patient modal (880px).
            <div className="flex-center">
              <h1>{t("wizard.welcomePatient.title")}</h1>
              <div className="mt-30" style={{ maxWidth: 560, width: "100%", textAlign: "left" }}>
                <p>{t("wizard.welcomePatient.subtitle1", { organization })}</p>
              </div>
            </div>
          ) : (
            <div className="flex-center">
              <h1>
                {t(
                  hasFeature("orders") || hasFeature("ordersManagement") ? "wizard.welcomeOrders.title" : "wizard.welcome.title"
                )}
              </h1>
              <p className="mt-30">
                {t(
                  hasFeature("orders") || hasFeature("ordersManagement")
                    ? "wizard.welcomeOrders.subtitle1"
                    : "wizard.welcome.subtitle1",
                  { organization }
                )}
              </p>
              <p className="mt-30">
                {t(
                  hasFeature("orders") || hasFeature("ordersManagement")
                    ? "wizard.welcomeOrders.subtitle2"
                    : "wizard.welcome.subtitle2"
                )}
              </p>
            </div>
          ))}

        {/* PASSWORD */}
        {step === "password" && (
          <div className="login-container">
            <Form fields={passwordFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* Clinical Role Selection (Supervisors Only) */}
        {step === "clinicalRoleSelection" && (
          <div className="">
            <Form fields={supervisorClinicalRoleFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* PIN */}
        {step === "pin" && (
          <div className="login-container">
            <h5>{t("wizard.pin.title")}</h5>

            <div className="flex-center">
              <VerificationInput
                inputProps={{
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                }}
                length={4}
                validChars={"0-9"}
                placeholder=""
                autoFocus={true}
                onChange={setPin}
              />
            </div>
          </div>
        )}

        {/* Doctor Type */}
        {step === "doctorTypeCredentials" && (
          <div className="">
            <Form fields={credentialsFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* Select Note Types */}
        {step === "noteTypes" && (
          <div className="">
            <h5>{t("wizard.noteTypes.title")}</h5>

            <div className="table-container">
              <Table data={displayNoteTypes} config={config} />
            </div>
          </div>
        )}

        {/* Select Note Types */}
        {step === "noteStyles" && (
          <div className="">
            <h5>{t("wizard.styles.title")}</h5>

            <h3>{t("wizard.styles.hpi")}</h3>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.style")}</label>
              <select
                value={profile.custom.noteSettings.default.hpiStyleMode}
                onChange={(e) => changeStyle("hpiStyleMode", e.target.value)}
              >
                <option value="paragraph">{t("wizard.styles.paragraph")}</option>
                <option value="bullets">{t("wizard.styles.bullets")}</option>
              </select>
            </div>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.length")}</label>
              <select
                value={profile.custom.noteSettings.default.hpiLengthMode}
                onChange={(e) => changeStyle("hpiLengthMode", e.target.value)}
              >
                <option value="comprehensive">{t("wizard.styles.comprehensive")}</option>
                <option value="concise">{t("wizard.styles.concise")}</option>
              </select>
            </div>

            <h3>{t("wizard.styles.mdm")}</h3>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.style")}</label>
              <select
                value={profile.custom.noteSettings.default.mdmStyleMode}
                onChange={(e) => changeStyle("mdmStyleMode", e.target.value)}
              >
                <option value="paragraph">{t("wizard.styles.paragraph")}</option>
                <option value="bullets">{t("wizard.styles.bullets")}</option>
              </select>
            </div>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.length")}</label>
              <select
                value={profile.custom.noteSettings.default.mdmLengthMode}
                onChange={(e) => changeStyle("mdmLengthMode", e.target.value)}
              >
                <option value="comprehensive">{t("wizard.styles.comprehensive")}</option>
                <option value="concise">{t("wizard.styles.concise")}</option>
              </select>
            </div>

            <h3>{t("wizard.styles.ros")}</h3>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.length")}</label>
              <select
                value={profile.custom.noteSettings.default.rosMode}
                onChange={(e) => changeStyle("rosMode", e.target.value)}
              >
                <option value="concise">{t("wizard.styles.concise")}</option>
                <option value="complex">{t("wizard.styles.complex")}</option>
              </select>
            </div>

            <h3>{t("wizard.styles.physicalExam")}</h3>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.length")}</label>
              <select
                value={profile.custom.noteSettings.default.physicalExamLengthMode}
                onChange={(e) => changeStyle("physicalExamLengthMode", e.target.value)}
              >
                <option value="comprehensive">{t("wizard.styles.comprehensive")}</option>
                <option value="concise">{t("wizard.styles.concise")}</option>
              </select>
            </div>

            <h3>{t("wizard.styles.planOfCare")}</h3>
            <div className="row-form vertical-align">
              <label>{t("wizard.styles.length")}</label>
              <select
                value={profile.custom.noteSettings.default.planOfCareMode}
                onChange={(e) => changeStyle("planOfCareMode", e.target.value)}
              >
                <option value="concise">{t("wizard.styles.concise")}</option>
                <option value="complex">{t("wizard.styles.complex")}</option>
              </select>
            </div>
          </div>
        )}

        {/* Profile */}
        {step === "personalInformation" && (
          <div className="">
            <Form fields={personalInfoFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* Language */}
        {step === "language" && (
          <div className="">
            <Form fields={lanuageFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* Referral */}
        {step === "referral" && (
          <div className="">
            <Form fields={referralFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* Patient Identity + Contact Preferences */}
        {step === "patientIdentityContact" && (
          <div className="">
            <Form fields={patientIdentityContactFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* Patient Shipping (+ optional billing) */}
        {step === "patientShipping" && (
          <div className="">
            <Form fields={patientShippingFormConfig} isValid={isValid} setIsValid={setIsValid} />
          </div>
        )}

        {/* BAA Steps (10, 11, ...) */}
        {["lucasBaa", "shorelineBaa", "meridianBaa", "gtmBaa", "optimalcaregrpBaa"].includes(step) &&
          (() => {
            const baaType = step.replace("Baa", "");
            return (
              <div>
                <BaaAgreementText type={baaType} />
                {isBaaSigned(baaType) ? (
                  <div className="mt-50 vertical-align badge green-dark">
                    <i className="material-symbols-outlined mr-10">signature</i>
                    {t("baa.signedBadge", {
                      version: profile.custom[`baaVersion_${baaType}`],
                      date: moment(profile.custom[`baaSignatureDate_${baaType}`]).format(defaultDateFormat),
                      name: profile.custom[`baaSignature_${baaType}`],
                    })}
                  </div>
                ) : (
                  <>
                    <div className="mt-30 badge red">{t("baa.notSigned")}</div>
                    <div className="mt-30 btn-bar flex-center">
                      <button className="btn btn-primary" onClick={() => signBaa(baaType)}>
                        {t("baa.sign")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

        {/* Terms Step (final) */}
        {step === "baaAndTerms" && (
          <div>
            <TermsOfUseText />
            {/* "Signed" requires BOTH the signature date AND the current version
                stamp (mirrors isBaaSigned). A half-signed profile — date saved
                but version dropped by the old junk-brand bug — must see the
                Sign button again, or the account is permanently trapped: Finish
                would re-send the stale signature with no version, forever. */}
            {profile.custom?.doctorConsentAuthorizeSignatureDate &&
            profile.custom?.doctorConsentAuthorizeVersion === currentTermsVersion ? (
              <div className="mt-50 vertical-align badge green-dark">
                <i className="material-symbols-outlined mr-10">signature</i>
                {t("terms.signedBadge", {
                  version: profile.custom.doctorConsentAuthorizeVersion,
                  date: moment(profile.custom?.doctorConsentAuthorizeSignatureDate).format(defaultDateFormat),
                  name: profile.custom.doctorConsentAuthorizeSignature,
                })}
              </div>
            ) : (
              <div className="mt-30 btn-bar flex-center">
                <button className="btn btn-primary" onClick={sign}>
                  {t("terms.sign")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="modal-actions btn-bar">
        {currentStepIndex !== 0 ? (
          <button className="white" onClick={prevStep}>
            {t("common.back")}
          </button>
        ) : (
          <div></div>
        )}
        {currentStepIndex < enabledStepNumbers.length - 1 ? (
          <button className="btn btn-primary" onClick={nextStep}>
            {t("wizard.buttons.next")}
          </button>
        ) : (
          <button className="btn btn-primary" onClick={complete}>
            {t("wizard.buttons.finish")}
          </button>
        )}
      </div>
    </Modal>
  );
}

export default ModalWizard;
